import { useQuery } from '@tanstack/react-query';
import type { BookmarkCollectionResponse } from '../types/api';

export interface SearchParams {
  q?: string;
  site?: string;
  author?: string;
  sort?: 'relevance' | 'oldest' | 'newest';
  page?: number;
}

/** Search params for the /search routes. `page` is omitted from the URL on page
 *  1 (canonical list-URL convention), so it's optional here. */
export interface SearchRouteSearch {
  q: string;
  site: string;
  author: string;
  sort: 'relevance' | 'oldest' | 'newest';
  page?: number;
}

/** Shared validateSearch for the /search route and its /search/read/$id child so
 *  the two never drift. */
export function validateSearchParams(search: Record<string, unknown>): SearchRouteSearch {
  const parsedPage = search.page ? parseInt(search.page as string, 10) || 1 : 1;
  return {
    q: (search.q as string) || '',
    site: (search.site as string) || '',
    author: (search.author as string) || '',
    sort: (search.sort as 'relevance' | 'oldest' | 'newest') || 'relevance',
    ...(parsedPage > 1 ? { page: parsedPage } : {}),
  };
}

export interface SearchResponse extends BookmarkCollectionResponse {
  count: number;
  page: number;
  has_more: boolean;
  query: string;
  sort: string;
}

export async function searchBookmarks(params: SearchParams): Promise<SearchResponse> {
  const searchParams = new URLSearchParams();

  if (params.q) searchParams.set('q', params.q);
  if (params.site) searchParams.set('site', params.site);
  if (params.author) searchParams.set('author', params.author);
  if (params.sort) searchParams.set('sort', params.sort);
  if (params.page && params.page > 1) searchParams.set('page', params.page.toString());

  const response = await fetch(`/data/search?${searchParams}`);

  if (response.status === 403) {
    throw new Error('PREMIUM_REQUIRED');
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Search failed: ${errorText}`);
  }

  return response.json();
}

export interface UseSearchOptions {
  enabled?: boolean;
}

export function useSearch(params: SearchParams, options: UseSearchOptions = {}) {
  const hasQuery = Boolean(params.q || params.site || params.author);

  return useQuery({
    queryKey: ['search', params],
    queryFn: () => searchBookmarks(params),
    enabled: hasQuery && options.enabled !== false,
    staleTime: 30000, // Cache results for 30 seconds
    retry: (failureCount, error) => {
      // Don't retry premium required errors
      if (error.message === 'PREMIUM_REQUIRED') {
        return false;
      }
      return failureCount < 3;
    },
  });
}
